import { Availability, type ShippingDelay } from '@gql-generated/graphql'

export type GetProductAvailabilitiesViewsProps = {
  product: {
    stock?: {
      availability: Array<Availability | null>
      storeQuantity?: number | null
      shippingDelay?: ShippingDelay | null
    } | null
    canAddToCart: boolean
  }
}

export type GetProductAvailabilitiesViewsReturn = {
  isOnlineHiddenByData: boolean
  isStoreHiddenByData: boolean
}

type PricesDisplayAvailabilityProps = {
  product: {
    stock?: { isPriceAvailable?: boolean | null } | null
    canAddToCart?: boolean | null
    filters?: Array<unknown> | null
  }
  isPublicPriceEnabled: boolean
  isAuthenticated?: boolean
  isProduct?: boolean
}

/**
 * Determines if online and store availabilities should be displayed based on product data.
 *
 * @param {GetProductAvailabilitiesViewsProps} props - Product data
 * @returns {GetProductAvailabilitiesViewsReturn} Object indicating if online and store availabilities should be hidden
 */
export function getProductAvailabilitiesViews({
  product,
}: GetProductAvailabilitiesViewsProps): GetProductAvailabilitiesViewsReturn {
  // stock may be missing while the price/stock query is still loading
  const { availability, storeQuantity } = product.stock ?? {}

  let isOnlineHiddenByData = true
  let isStoreHiddenByData = true

  if (
    [Availability.Store, Availability.StoreOnly, Availability.StoppedInstore].some((a) =>
      availability?.includes(a),
    )
  ) {
    isStoreHiddenByData = false

    // if StoreOnly and storeQuantity < 1, hide store availability
    if (availability?.includes(Availability.StoreOnly) && (!storeQuantity || storeQuantity < 1)) {
      isStoreHiddenByData = true
    }
  }

  if ([Availability.Online, Availability.StoppedOnline].some((a) => availability?.includes(a))) {
    isOnlineHiddenByData = false
  }

  return { isOnlineHiddenByData, isStoreHiddenByData }
}

/**
 * Determines if the product is available for sale based on its availabilities.
 *
 * @param {GetProductAvailabilitiesViewsProps} props - Product data
 * @returns {boolean} - True if the product is available for sale, false otherwise
 */
export function isProductAvailableForSale({
  product,
}: GetProductAvailabilitiesViewsProps): boolean {
  return product.canAddToCart
}

/**
 * Determines if prices should be displayed based on product data and user/context flags.
 *
 * @param {PricesDisplayAvailabilityProps} props - Product data and flags
 *
 * @returns {boolean} - True if prices can be shown, false otherwise. Always true for multi-discriminant products.
 */
export function shouldDisplayProductPrices({
  product,
  isPublicPriceEnabled,
  isAuthenticated,
  isProduct,
}: PricesDisplayAvailabilityProps): boolean {
  const discriminantsCount = product.filters?.length ?? 0
  const isMultiDiscriminantProduct = isProduct && discriminantsCount > 1

  // Multi-discriminant products always display prices (prevents availabilityIssueCard)
  if (isMultiDiscriminantProduct) {
    return true
  }

  const userHasPriceAccess = Boolean(
    isPublicPriceEnabled || (product.canAddToCart && isAuthenticated),
  )

  // Hide prices only when the mesh explicitly says no price is available
  // AND the user would normally have access to prices (authenticated or public prices enabled).
  // For guests on non-public-price sites, userHasPriceAccess is false so this never blocks —
  // the caller (getAvailabilityIssue) handles that via the isPriceAvailable check instead.
  return !(product.stock?.isPriceAvailable === false && userHasPriceAccess)
}

type AvailabilitiesDisplayProps = {
  hasPrice: boolean
  hasAvailabilityIssue: boolean
  isAuthenticated: boolean
  isPublicStockEnabled: boolean
}

/**
 * Determines if availabilities should be displayed, independently of price visibility:
 * guests on public-stock sites see them even when prices stay hidden.
 */
export function shouldDisplayProductAvailabilities({
  hasPrice,
  hasAvailabilityIssue,
  isAuthenticated,
  isPublicStockEnabled,
}: AvailabilitiesDisplayProps): boolean {
  return hasPrice || (!isAuthenticated && isPublicStockEnabled && !hasAvailabilityIssue)
}
